/**
 * tripUtils.ts - Pure utility functions for trip assignment.
 * Extracted from AssignTripModal.tsx
 */

/** Check if a location is an airport (BGO, HAU, etc.) */
export function isAirportLocation(location: any): boolean {
  if (!location || typeof location !== 'string') return false;
  const airportCodes = ['bgo', 'hau', 'svg', 'osl', 'trd', 'flesland', 'sola', 'gardermoen', 'værnes', 'karmøy'];
  const lowerLoc = location.toLowerCase();
  return airportCodes.some(code => lowerLoc.includes(code)) ||
    lowerLoc.includes('lufthavn') ||
    lowerLoc.includes('airport');
}

/** Check if a location is a warehouse/lager (e.g., 127ELDØY) */
export function isWarehouseLocation(location: any): boolean {
  if (!location || typeof location !== 'string') return false;
  const lowerLoc = location.toLowerCase();
  return lowerLoc.includes('127') || lowerLoc.includes('lager');
}

/** Normalisering som backend `normalize_location_for_comparison` (små bokstaver, ø→o, æ→ae, å→a). */
const normLoc = (s: any) => String(s || '').toLowerCase().replace(/ø/g, 'o').replace(/æ/g, 'ae').replace(/å/g, 'a').trim();
const compactLoc = (n: string) => n.replace(/[^a-z0-9]/g, '');
const SUFFIX_TOKENS = ['camp', 'lager', 'busstopp', 'flyplass', 'airport', 'lufthavn'];
const AIRPORT_SUFFIX = ['flyplass', 'airport', 'lufthavn'];

type CanonIndex = { exact: Map<string, string>; compact: Map<string, string>; airports: Set<string>; src: any[] };
let _canonIndex: CanonIndex | null = null;

function canonIndex(locationShortcuts: any[]): CanonIndex {
  if (_canonIndex && _canonIndex.src === locationShortcuts) return _canonIndex;
  const exact = new Map<string, string>(); const compact = new Map<string, string>(); const airports = new Set<string>();
  for (const s of locationShortcuts || []) {
    const code = String(s?.shortcut || '').trim();
    if (!code) continue;
    const key = code.toUpperCase();
    const addr = String(s.full_address || '');
    const cands = [s.shortcut, s.name, s.full_address, addr.includes(',') ? addr.split(',')[0] : '', ...(s.variations || [])];
    for (const c of cands) {
      const n = normLoc(c);
      if (!n) continue;
      if (!exact.has(n)) exact.set(n, key);
      const cp = compactLoc(n);
      if (cp && !compact.has(cp)) compact.set(cp, key);
    }
    if (isAirportLocation(code) || isAirportLocation(s.name) || isAirportLocation(s.full_address)) airports.add(key);
  }
  _canonIndex = { exact, compact, airports, src: locationShortcuts };
  return _canonIndex;
}

function resolveText(n: string, idx: CanonIndex): string | null {
  const k = idx.exact.get(n) || idx.compact.get(compactLoc(n));
  if (k) return k;
  const words = n.replace(/,/g, ' ').split(/\s+/).filter(Boolean);
  if (words.length >= 2 && SUFFIX_TOKENS.includes(words[words.length - 1])) {
    const base = words.slice(0, -1).join(' ');
    const bk = idx.exact.get(base) || idx.compact.get(compactLoc(base));
    if (bk && (!AIRPORT_SUFFIX.includes(words[words.length - 1]) || idx.airports.has(bk))) return bk;
  }
  return null;
}

/**
 * Get canonical shortcut key for a location by checking against database shortcuts.
 * Ensures "BGO", "Bergen Flyplass", "Flyplassvegen 555" all resolve to the same key.
 * Iter 850: speiler backend `CanonMap.resolve` — kode/navn/variant/adresse (eksakt → uten mellomrom/tegn → uten
 * suffiks «Camp»/«Lager»), og kommaseparerte adresser («Eldøyane, Stord, Norge») via FØRSTE ledd. Aldri delstreng.
 * Returnerer forkortelseskoden (store bokstaver) eller teksten i små bokstaver når ingen Forkortelse treffer.
 */
export function getCanonicalLocationKeyFn(location: any, locationShortcuts: any[]): string {
  if (!location || typeof location !== 'string') return '';
  const lowerLoc = location.toLowerCase().trim();
  if (!locationShortcuts || locationShortcuts.length === 0) return lowerLoc;
  const idx = canonIndex(locationShortcuts);
  const n = normLoc(location);
  if (!n) return '';
  const k = resolveText(n, idx);
  if (k) return k;
  if (n.includes(',')) {
    const head = n.split(',')[0].trim();
    if (head && head !== n) {
      const hk = resolveText(head, idx);
      if (hk) return hk;
    }
  }
  return lowerLoc;
}

/** Iter 850/856: bookingens stopp i TURENS kolonner (kanonisk nøkkel via `keyFn`). Kolonne i = turens St(i); tom (`unused`)
 *  når bookingen ikke har stoppet. Faller tilbake til `fallback` (lagret posisjon) når bookingen har stopp utenfor ruten.
 *  Egen Fra/Til midt på ruten hører hjemme i den kolonnen. Ventende kundestopp vises som `pending`.
 *  Delt mellom Tilegn tur (TripBookingsTable) og Ukesplanens undertabell (BookingDetailsTable). */
export function alignBookingToRoute(b: any, routeStops: any[] | undefined, keyFn: ((l: string) => string) | undefined, fallback: any[],
  pending: { location: string }[] = []): { stops: any[]; aligned: boolean } {
  if (!routeStops || routeStops.length < 2 || !keyFn) return { stops: fallback, aligned: false };
  const src = Array.isArray(b.original_stops) && b.original_from_location ? b.original_stops : (b.stops || []);
  const own = new Map<string, any>();
  for (const s of src) {
    const loc = typeof s === 'string' ? s : s?.location;
    if (loc && !(s?.unused === true) && s?.off_route !== true && s?.stop_type !== 'placeholder') own.set(keyFn(loc), typeof s === 'string' ? { location: s } : s);
  }
  for (const p of pending) own.set(keyFn(p.location), { location: p.location, time: '', pending: true });
  const first = keyFn(routeStops[0]?.location || ''); const last = keyFn(routeStops[routeStops.length - 1]?.location || '');
  const ownFrom = b.original_from_location || b.from_location; const ownTo = b.original_to_location || b.to_location;
  if (ownFrom && keyFn(ownFrom) !== first) own.set(keyFn(ownFrom), { location: ownFrom, time: b.original_departure_time || b.departure_time || '' });
  if (ownTo && keyFn(ownTo) !== last) own.set(keyFn(ownTo), { location: ownTo, time: '' });
  const mids = routeStops.slice(1, -1);
  const known = new Set([first, last, ...mids.map((rs: any) => keyFn(rs.location))]);
  if ([...own.keys()].some((k) => !known.has(k))) return { stops: fallback, aligned: false };
  const label = (loc: string) => { const k = keyFn(loc); return k && k !== String(loc).toLowerCase().trim() ? k : loc; };
  const stops = mids.map((rs: any) => {
    const s = own.get(keyFn(rs.location));
    return s ? { location: label(rs.location), time: s.time || rs.time || '', pending: !!s.pending } : { location: '', time: '', unused: true };
  });
  return { stops, aligned: true };
}

/** Visningsetikett = forkortelseskoden når teksten treffer en Forkortelse, ellers teksten selv (som backend `canon_label`). */
export function canonicalLocationLabel(location: any, locationShortcuts: any[]): string {
  const raw = String(location || '').trim();
  if (!raw) return '';
  const k = getCanonicalLocationKeyFn(raw, locationShortcuts);
  return k && k !== raw.toLowerCase() ? k : raw;
}

/**
 * Calculate estimated cost for an assignment based on driver/vehicle/route.
 * Returns { total, breakdown }.
 */
export function calculateAssignmentCostFn(
  assignment: any,
  drivers: any[],
  externalCompanies: any[],
  organizedStops: any[],
  tripCost: any,
  getCanonicalLocationKey: (loc: string) => string
): { total: number; breakdown: any } {
  if (assignment.driverType === 'internal') {
    if (!assignment.driverId || assignment.driverId === '' || assignment.driverId === 'unknown' || assignment.driverId === 'none') {
      return { total: 0, breakdown: null };
    }
    const driver = drivers.find((d: any) => d.id === assignment.driverId);
    const hourlyRate = driver?.hourly_rate || 0;
    const estimatedHours = tripCost?.duration_hours || 2;
    const driverCost = hourlyRate * estimatedHours;
    const fuelCost = tripCost?.fuel_cost || 0;
    const tollCost = tripCost?.toll_cost || 0;
    const ferryCost = tripCost?.ferry_cost || 0;
    const distanceKm = tripCost?.distance_km || 0;
    return {
      total: Math.round(driverCost + fuelCost + tollCost + ferryCost),
      breakdown: {
        driverName: driver?.name, hourlyRate,
        estimatedHours: Math.round(estimatedHours * 10) / 10,
        driverCost: Math.round(driverCost),
        distanceKm: Math.round(distanceKm),
        fuelCost: Math.round(fuelCost), tollCost: Math.round(tollCost), ferryCost: Math.round(ferryCost) } };
  } else {
    if (!assignment.externalCompanyId) return { total: 0, breakdown: null };
    const extCompany = externalCompanies.find((c: any) => c.id === assignment.externalCompanyId);
    if (!extCompany) return { total: 0, breakdown: null };

    if (extCompany.route_prices?.length > 0) {
      const fromLoc = organizedStops[0]?.location || '';
      const toLoc = organizedStops[organizedStops.length - 1]?.location || '';
      const tripFromKey = getCanonicalLocationKey(fromLoc);
      const tripToKey = getCanonicalLocationKey(toLoc);
      const routePrice = extCompany.route_prices.find((rp: any) => {
        const rpFromKey = getCanonicalLocationKey(rp.from_location || '');
        const rpToKey = getCanonicalLocationKey(rp.to_location || '');
        return (rpFromKey === tripFromKey && rpToKey === tripToKey) || (rpFromKey === tripToKey && rpToKey === tripFromKey);
      });
      if (routePrice?.price) {
        return {
          total: routePrice.price,
          breakdown: { type: 'external_route', companyName: extCompany.company_name || extCompany.name, routePrice: routePrice.price, fromLoc: routePrice.from_location, toLoc: routePrice.to_location } };
      }
    }
    if (extCompany.default_price) {
      return { total: extCompany.default_price, breakdown: { type: 'external_default', companyName: extCompany.company_name || extCompany.name, defaultPrice: extCompany.default_price } };
    }
    if (extCompany.price_per_km && tripCost?.distance_km) {
      const kmPrice = Math.round(extCompany.price_per_km * tripCost.distance_km);
      return { total: kmPrice, breakdown: { type: 'external_km', companyName: extCompany.company_name || extCompany.name, pricePerKm: extCompany.price_per_km, distanceKm: Math.round(tripCost.distance_km), total: kmPrice } };
    }
    return { total: 0, breakdown: null };
  }
}
